'use client'

import Image from 'next/image'
import { cn } from '@/lib/utils'
import {
  FILTER_PRESETS,
  filterSettingsToCSS,
  applyPreset,
} from './filterPresets'
import type { FilterPreset } from './filterPresets'

interface PresetFiltersProps {
  /** URL of the image to show in thumbnails */
  imageUrl: string
  /** Currently active preset ID */
  activePreset: string | null
  /** Called when a preset is selected */
  onSelect: (preset: FilterPreset) => void
}

/**
 * PresetFilters
 *
 * Grid of one-click filter presets with live preview thumbnails
 */
export function PresetFilters({
  imageUrl,
  activePreset,
  onSelect,
}: PresetFiltersProps) {
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
      {FILTER_PRESETS.map((preset) => {
        const isActive = activePreset === preset.id
        const filterCSS = filterSettingsToCSS(applyPreset(preset))

        return (
          <button
            key={preset.id}
            onClick={() => onSelect(preset)}
            className={cn(
              'flex flex-col items-center gap-1.5 p-1.5 rounded-lg',
              'transition-all duration-150 touch-manipulation',
              'hover:bg-muted',
              isActive && 'ring-2 ring-primary bg-muted'
            )}
          >
            {/* Preview thumbnail - optimized with next/image */}
            <div
              className={cn(
                'relative w-full aspect-square rounded-md overflow-hidden',
                'bg-muted border border-border'
              )}
              style={{ filter: filterCSS }}
            >
              <Image
                src={imageUrl}
                alt={preset.name}
                fill
                sizes="100px"
                className="object-cover"
                loading="lazy"
              />
            </div>

            {/* Preset name */}
            <span
              className={cn(
                'text-xs font-medium truncate w-full text-center',
                isActive ? 'text-primary' : 'text-muted-foreground'
              )}
            >
              {preset.name}
            </span>
          </button>
        )
      })}
    </div>
  )
}
